import React, {
  useState,
  useEffect,
  ChangeEvent,
  useCallback,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';

import clsx from 'clsx';

import { messageIdConcat } from '@utils/message-id-concat';
import { Message } from '@utils/message';
import { areRangesEqual, validateRange } from '@utils/filter-ranges';

import { Inline, Text } from '@atoms';
import { Input } from '@molecules/input';
import { Button } from '@molecules/button';
import { RadioItem, RadioItemProps } from '@molecules/radio-item';
import { RadioGroup } from '@molecules/radio-group';

import { FilterRange } from '@typings/filters';

import { RangeFilterFormRef } from './range-filter-form.types';

import classes from './range-filter-form.module.scss';

const m = messageIdConcat('filters.range_filter');

export type RadioChoice = {
  item: RadioItemProps;
  range: FilterRange;
};

type Props = {
  range?: FilterRange;
  onRangeChange: (range: FilterRange | undefined) => void;
  placeholders?: {
    from: Message;
    to: Message;
  };
  radioButtons?: RadioChoice[];
  suffixUnits?: Message;
  simplified?: boolean;
  submit?: (handler: (range: FilterRange | undefined) => void) => void;
};

const parseRangeItem = (value: string) => {
  if (value === '') return null;
  const parsed = Number.parseInt(value);
  if (Number.isNaN(parsed)) return null;
  return parsed;
};

const RangeFilterFormWithRef = (
  {
    range,
    onRangeChange,
    placeholders,
    radioButtons = [],
    suffixUnits,
    simplified,
  }: Props,
  fwRef: RangeFilterFormRef,
) => {
  const [rangeState, setRangeState] = useState<FilterRange | undefined>(range);
  const [inputError, setInputError] = useState(false);

  // ensure incoming range will review internal state
  useEffect(() => {
    setRangeState(range);
  }, [range]);

  const activeChoice = useMemo(() => {
    if (!rangeState) return;
    return radioButtons.find(({ range }) => areRangesEqual(range, rangeState));
  }, [radioButtons, rangeState]);

  /**
   * Handlers
   * - onSubmit - submits form
   * - onKeyDown - watch keydown for Enter submit
   * - onChange - universal for all inputs
   * - onChangeFrom - for from input
   * - onChangeTo - for to input
   */
  // on submit button
  const validate = useCallback((range: typeof rangeState) => {
    if (range) return validateRange(range);
    return true;
  }, []);

  const onSubmit = useCallback(
    (range) => {
      // validate
      if (!simplified) {
        const valid = validate(range);
        if (!valid) return;
      }
      // dispatch valid change
      onRangeChange(range);
    },
    [onRangeChange, validate],
  );
  // on enter submit
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') onSubmit(rangeState);
    },
    [onSubmit, rangeState],
  );
  // on change
  const onChange = useCallback(
    (params: { from?: string; to?: string }) => {
      let from = (rangeState && rangeState[0]) || null;
      let to = (rangeState && rangeState[1]) || null;
      if (typeof params.from !== 'undefined')
        from = parseRangeItem(params.from);
      if (typeof params.to !== 'undefined') to = parseRangeItem(params.to);

      const newRange = [from, to];

      setInputError(false);
      // in case of filter clear
      if (newRange[0] === null && newRange[1] === null) {
        setRangeState(undefined);
        if (simplified) onSubmit(undefined);
        return;
      }

      setRangeState(newRange as FilterRange);
      if (simplified) onSubmit(newRange);
    },
    [rangeState, onSubmit, simplified],
  );
  const onChangeFrom = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onChange({ from: e.target.value }),
    [onChange],
  );
  const onChangeTo = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onChange({ to: e.target.value }),
    [onChange],
  );

  const onOptionSelect = useCallback(
    (option: string): void => {
      const radioButton = radioButtons.find(
        ({ item }) => item.value === option,
      );
      if (radioButton && radioButton.range) {
        setRangeState(radioButton.range);
        if (simplified) onSubmit(radioButton.range);
      }
    },
    [onSubmit, radioButtons, simplified],
  );

  // ref
  useImperativeHandle(
    fwRef,
    () => ({
      validate: () => validate(range),
    }),
    [validate, range],
  );

  return (
    <div
      className={clsx(
        classes.rangeFilter,
        simplified && classes.rangeFilterSimplified,
      )}
    >
      {/* INPUTS */}
      {radioButtons.length > 0 && (
        <div>
          <RadioGroup
            className={classes.radioContainer}
            value={activeChoice && activeChoice.item.value}
            onChange={onOptionSelect}
          >
            {radioButtons.map((radioItem) => (
              <RadioItem
                key={String(radioItem.item.value)}
                bigger
                value={radioItem.item.value}
                message={radioItem.item.message}
              />
            ))}
          </RadioGroup>
        </div>
      )}
      <div className={classes.form}>
        <div className={classes.formItem}>
          <Text
            margin={0}
            color="slate_gray"
            type="reduced"
            message={{ id: m('from') }}
          />
          <Input
            type="text"
            value={rangeState && rangeState[0] ? `${rangeState[0]}` : ''}
            onChange={onChangeFrom}
            onKeyDown={onKeyDown}
            placeholder={placeholders && placeholders.from}
            className={clsx(
              classes.input,
              simplified && classes.inputSimplified,
            )}
            border="heather"
            withoutErrorMessage
            withoutTouched
            error={inputError ? {} : undefined}
            touched
            onClear={simplified ? () => onChange({ from: '' }) : undefined}
            suffixUnit={suffixUnits}
            formattedPrice
          />
        </div>
        <div className={classes.formItem}>
          <Inline type="reduced" color="slate_gray" message={{ id: m('to') }} />
          <Input
            type="text"
            value={rangeState && rangeState[1] ? `${rangeState[1]}` : ''}
            onChange={onChangeTo}
            onKeyDown={onKeyDown}
            placeholder={placeholders && placeholders.to}
            className={clsx(
              classes.input,
              simplified && classes.inputSimplified,
            )}
            border="heather"
            withoutErrorMessage
            withoutTouched
            error={inputError ? {} : undefined}
            touched
            onClear={simplified ? () => onChange({ to: '' }) : undefined}
            suffixUnit={suffixUnits}
            formattedPrice
          />
        </div>
      </div>
      {/* CONTROLS */}
      {!simplified && (
        <div className={classes.controls}>
          <span
            style={{ cursor: 'pointer' }}
            onClick={() => {
              onRangeChange(undefined);
            }}
          >
            <Text
              margin={0}
              type="reduced"
              message={{ id: m('dismiss') }}
              fontWeight={rangeState ? 600 : 400}
              color={rangeState ? 'azure_radiance' : 'big_stone'}
            />
          </span>
          <Button
            onClick={() => onSubmit(rangeState)}
            size="small"
            message={{ id: m('submit') }}
          />
        </div>
      )}
    </div>
  );
};

export const RangeFilterForm = forwardRef(RangeFilterFormWithRef);
